import { NotifierTypes } from 'constants/notifierConstants';
import {
  selectAreCustomizationsReady,
  selectCurrentTeam,
  selectInvoiceLimit,
  selectIsActionsBlocked,
  selectMerchantCustomizations,
  showNotifier
} from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { FC, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import routesPath from 'routes/RoutesPath';
import { InvoicesRouteState } from 'routes/types';
import { LoadingSpinnerAnimation } from 'assets/animations';
import GenericError from 'components/genric-error/GenericError';
import { useEditInvoiceMutation, useLazyGetInvoiceByIdV2Query } from '../api';
import { PreviewBar } from '../components';
import { InvoiceTabEnum, minimumLineItemsError } from '../create-invoice/constants';
import {
  EditInvoiceWithRiskValidationResponse,
  RISK_VALIDATION_ENTITY,
  RiskValidationErrorResponse
} from '../invoices.model';
import InvoiceOptionsSectionEdit from './components/InvoiceOptionsSectionEdit';
import InvoicesTabSideNav from '../create-invoice/components/InvoicesTabSideNav';
import LineItemsSectionEdit from './components/LineItemsSectionEdit';
import NotesAttachmentsSectionEdit from './components/NotesAttachmentsSectionEdit';
import PaymentOptionsSectionEdit from './components/PaymentOptionsSectionEdit';
import InvoiceFormRemindersSection from '../create-invoice/components/InvoiceFormRemindersSection';
import { throttle } from 'utils/commonUtils';
import {
  addInvoiceValidationErrorEditPage,
  enableCustomerNotSelectedErrorEditPage,
  initialiseInvoicePaymentModesEdit,
  resetInvoiceEditSlice,
  selectAppliedLineItemLevelTaxesEditPage,
  selectEstimateNumberForInvoiceEditPage,
  selectEstimateStatusForInvoiceEditPage,
  selectInvoiceEditPage,
  selectInvoiceFinalAmountEditPage,
  selectInvoicePaymentModesEdit,
  selectInvoiceReferenceNumberEditPage,
  selectInvoiceSliceInitializationInProgress,
  selectIsBackFromCreateTaxInEditSlice,
  selectIsFromPreview,
  selectIsInvoiceFromJobEditPage,
  selectIsSignatureEnabledEditPage,
  selectJobDataForInvoiceEditPage,
  selectJobNumberForInvoiceEditPage,
  selectPartialPaymentEnabledEditPage,
  toggleIsFromPreview,
  updateInvoiceEditedResponse,
  updateInvoiceFeeSettingsEditPageFromCustomizations,
  updateInvoiceInitializationInProgress,
  updateInvoiceReferenceNumberEditPage,
  updateIsSignatureEnabledEditPage,
  updatePartialPaymentEnabledEditPage,
  updateSelectedTNC
} from './invoicesEditSlice';
import { updateInvoiceDefaultsFromSetting } from '../InvoicesSlice';
import { ErrorResponse } from 'containers/app/app.model';
import { logAnalyticEvent } from 'utils/analytics';
import { RiskControlActionType, webInvoiceCreateRiskControl } from '../events';
import {
  checkIfAnyPaymentModeIsEligible,
  checkIfInvoiceTotalExceedsPaymentModeLimit,
  doesServiceChargeNameContainTax,
  getInvoiceThresholdValidationErrors,
  hasChangesToSync
} from '../utils';
import { DispatchDestination, invoiceMinimumAmount } from 'constants/common';
import { useGetItemSuggestionsAndGlobalTaxesAndFeesQuery } from 'containers/product-catalog/api';
import MoreOptionsSection from '../create-invoice/components/MoreOptionsSection';
import { FeatureToggleState, PartialPaymentOption } from 'containers/settings/constants';
import { useLazyGetInvoiceSettingsQuery } from 'containers/settings/invoice-settings/api';
import { transformSettingApiToComponentReminders } from 'containers/settings/utils';
import { getEditInvoiceV2Payload } from './util';
import EditInvoiceCustomer from './components/EditInvoiceCustomer';
import { CrossIconvector } from 'assets/icons';
import { GenericModal } from 'components';
import NoSelfCheckoutPopup from '../components/NoSelfCheckoutPopup';
import SendUpdateModal from '../components/SendUpdateModal';
import { InvoiceErrorFieldsDefault } from '../create-invoice/types';
import InvoiceErrorsPopup from '../components/InvoiceErrorsPopup';
import InvoiceRiskWarningsPopup from '../components/InvoiceRiskWarningsPopup';
import InvoiceTaxEncouragedPopup from '../components/InvoiceTaxEncouragedPopup';
import InvoiceLimitExceededPopup from '../components/InvoiceLimitExceededPopup';
import PartialPaymentAvailablePopup from '../components/PartialPaymentAvailablePopup';
import { updateCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { EstimateStatus } from 'containers/estimates/constants';
import BlockedInfoPopup from 'containers/home/components/BlockedInfoPopup';
import InvalidInvoiceTotalModal from '../components/InvalidInvoiceTotalModal';
import { selectInvoiceByIdRawDataV2 } from 'containers/payments-side-panel/sidePanelSlice';
import { StatusIndicator } from '../constants';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import { useSyncFeeSettingsFromCustomizations } from '../hooks/useSyncFeeSettingsFromCustomizations';

interface IEditInvoiceContext {
  selectedInvoiceTab: InvoiceTabEnum;
  setSelectedInvoiceTab: React.Dispatch<React.SetStateAction<InvoiceTabEnum>>;
}

export const EditInvoiceContext = createContext<IEditInvoiceContext>({
  selectedInvoiceTab: InvoiceTabEnum.BILL_TO,
  setSelectedInvoiceTab: null
});

interface EditInvoiceProps {
  goToPreviewPage: () => void;
}

const EditInvoice: FC<EditInvoiceProps> = ({ goToPreviewPage }) => {
  const invoiceInitInProgress = useAppSelector(selectInvoiceSliceInitializationInProgress);
  const isBackFromCreateTaxRoute = useAppSelector(selectIsBackFromCreateTaxInEditSlice);
  const selectedIsSignatureEnabled = useAppSelector(selectIsSignatureEnabledEditPage);
  const invoiceReferenceNumberEditPage = useAppSelector(selectInvoiceReferenceNumberEditPage);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const selectedInvoice = useAppSelector(selectInvoiceEditPage);
  const isFromPreview = useAppSelector(selectIsFromPreview);
  const isInvoiceFromJob = useAppSelector(selectIsInvoiceFromJobEditPage);
  const jobData = useAppSelector(selectJobDataForInvoiceEditPage);
  const invoiceFromJobNumber = useAppSelector(selectJobNumberForInvoiceEditPage);
  const estimateNumber = useAppSelector(selectEstimateNumberForInvoiceEditPage);
  const estimateStatus = useAppSelector(selectEstimateStatusForInvoiceEditPage);
  const isEstimatePending = useMemo(() => estimateStatus?.toUpperCase() === EstimateStatus.PENDING, [estimateStatus]);
  const partialPaymentEnabled = useAppSelector(selectPartialPaymentEnabledEditPage);
  const invoicePaymentModes = useAppSelector(selectInvoicePaymentModesEdit);
  const invoiceTotal = useAppSelector(selectInvoiceFinalAmountEditPage);
  const invoiceLimits = useAppSelector(selectInvoiceLimit);
  const invoiceRawDataV2 = useAppSelector(selectInvoiceByIdRawDataV2);
  const appliedLineItemLevelTaxes = useAppSelector(selectAppliedLineItemLevelTaxesEditPage);
  const customizationsReady = useAppSelector(selectAreCustomizationsReady);
  const merchantCustomisations = useAppSelector(selectMerchantCustomizations);

  const isInitialisationDone = useRef(false);

  // Refs holding the latest values used when building the edit-invoice payload.
  // `handleEditInvoice` is captured by several useCallback handlers whose dep
  // arrays don't react to in-place line item changes (e.g. description-only
  // edits). Reading from refs guarantees the latest state is always sent.
  const selectedInvoiceRef = useRef(selectedInvoice);
  selectedInvoiceRef.current = selectedInvoice;

  const hasCustomerEmail = useMemo(
    () => selectedInvoice.invoiceCustomerEditPage?.email?.trim() !== '',
    [selectedInvoice.invoiceCustomerEditPage?.email]
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(InvoiceTabEnum.BILL_TO);
  const [riskValidationErrors, setRiskValidationErrors] = useState<RiskValidationErrorResponse[]>([]);
  const [showRiskWarningsPopup, setShowRiskWarningsPopup] = useState(false);
  const [riskWarnings, setRiskWarnings] = useState<string[]>([]);
  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showNoSelfCheckoutPopup, setShowNoSelfCheckoutPopup] = useState(false);
  const [showInvoiceErrorsPopup, setShowInvoiceErrorsPopup] = useState({
    open: false,
    errors: InvoiceErrorFieldsDefault
  });
  const [showMerchantBlockedPopup, setShowMerchantBlockedPopup] = useState(false);
  const [showPartialPaymentAvailablePopup, setShowPartialPaymentAvailablePopup] = useState(false);
  const [showInvoiceLimitExceededPopup, setShowInvoiceLimitExceededPopup] = useState(false);
  const [showTaxEncouragedPopup, setShowTaxEncouragedPopup] = useState(false);
  const [showInvalidTotalModal, setShowInvalidTotalModal] = useState(false);
  const [showMarkAsPaidConfirmationPopup, setShowMarkAsPaidConfirmationPopup] = useState(false);
  const [showSendUpdateToCustomerModal, setShowSendUpdateToCustomerModal] = useState(false);

  const [getInvoiceQuery, getInvoiceQueryState] = useLazyGetInvoiceByIdV2Query();
  const [getInvoiceSettings] = useLazyGetInvoiceSettingsQuery();
  const [
    editInvoiceMutation,
    {
      isSuccess: isEditInvoiceSuccess,
      data: invoiceEditedResponse,
      isError: isInvoiceEditError,
      error: invoiceEditError,
      isLoading: isEditInvoiceLoading
    }
  ] = useEditInvoiceMutation();

  useGetItemSuggestionsAndGlobalTaxesAndFeesQuery({
    teamId: currentTeam.id,
    profession: currentTeam.profession,
    dispatchDestination: DispatchDestination.EDIT_INVOICE_SLICE
  });

  useSyncFeeSettingsFromCustomizations(updateInvoiceFeeSettingsEditPageFromCustomizations, {
    isReady: !invoiceInitInProgress && !isFromPreview,
    teamId: currentTeam?.id
  });

  useEffect(() => {
    if (
      customizationsReady &&
      selectedInvoice?.finalAmountEditPage &&
      !isFromPreview &&
      !isInitialisationDone.current
    ) {
      dispatch(updateCustomizations(merchantCustomisations));
      if (!invoiceInitInProgress) {
        if (merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON) {
          dispatch(updateIsSignatureEnabledEditPage(true));
        }
        dispatch(
          initialiseInvoicePaymentModesEdit({
            paymentModes: merchantCustomisations.paymentModes,
            finalAmount: selectedInvoice.finalAmountEditPage
          })
        );
      }
      isInitialisationDone.current = true;
    }
  }, [
    customizationsReady,
    invoiceInitInProgress,
    dispatch,
    selectedInvoice?.finalAmountEditPage,
    merchantCustomisations,
    isFromPreview
  ]);

  // Fetch invoice settings on mount
  useEffect(() => {
    if (currentTeam.id) {
      getInvoiceSettings({ teamId: currentTeam.id }).then(response => {
        if (response.data) {
          // Transform invoice reminders to component format using existing utility function
          const defaultReminders = transformSettingApiToComponentReminders(response.data.reminders || [], false);
          const settingsRemindersEnabled = response.data.remindersEnabled || false;
          const forcePartialPaymentSettings = response.data.partialPaymentStatus === PartialPaymentOption.ALWAYS_ON;
          // Store default reminders and settings config in Redux
          dispatch(
            updateInvoiceDefaultsFromSetting({
              reminders: defaultReminders,
              remindersEnabled: settingsRemindersEnabled,
              partialPayment: response.data.partialPaymentStatus,
              dueDateDuration: response.data.dueDate?.duration
            })
          );
          if (forcePartialPaymentSettings) {
            dispatch(updatePartialPaymentEnabledEditPage(true));
          }
        }
      });
    }
  }, [currentTeam.id, dispatch, getInvoiceSettings]);

  const pendingSkipWarningsRef = useRef(false);
  const invoiceCreatedSuccessRef = useRef(false);
  const lastShownValidationErrorsRef = useRef<typeof InvoiceErrorFieldsDefault | null>(null);

  const handleEditInvoiceSuccess = useCallback(
    (data: EditInvoiceWithRiskValidationResponse) => {
      setRiskValidationErrors([]);
      setShowRiskWarningsPopup(false);
      setRiskWarnings([]);
      dispatch(resetInvoiceEditSlice());
      dispatch(updateInvoiceEditedResponse(data));
      invoiceCreatedSuccessRef.current = true;
      logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.success });

      const shouldSkipShareModal = data.statusIndicator === StatusIndicator.FULLY_PAID;
      const invoiceRouteState: InvoicesRouteState = {
        showShareModal: !shouldSkipShareModal,
        isEditInvoiceShare: !shouldSkipShareModal,
        showSidePanel: false
      };

      if (shouldSkipShareModal) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Invoice marked as paid',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.SUCCESS,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
        navigate(`/invoices/${currentTeam.id}/${data.invoiceId}/invoice`, {
          replace: true,
          state: ROOT_ROUTE_STATE
        });
        return;
      }

      navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
    },
    [currentTeam.id, dispatch, navigate]
  );

  useEffect(() => {
    return () => {
      if (!invoiceCreatedSuccessRef.current) {
        logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.abandoned });
      }
    };
  }, []);

  useEffect(() => {
    const state = location.state as
      | { riskValidationErrors?: RiskValidationErrorResponse[]; riskWarnings?: string[] }
      | undefined;
    if (state?.riskValidationErrors?.length) {
      setRiskValidationErrors(state.riskValidationErrors);
      setRiskWarnings(state.riskWarnings ?? []);
      setShowRiskWarningsPopup(!!state.riskWarnings?.length);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const { data: getInvoiceResponse, isSuccess: getInvoiceSuccess, isError: isGetInvoiceErr } = getInvoiceQueryState;

  // Calculate net paid amount (total paid minus tip) for validation
  // Use invoiceRawDataV2 from Redux (persists across navigation) with fallback to getInvoiceResponse
  const netPaidAmount = useMemo(() => {
    const invoiceData = invoiceRawDataV2?.invoice || getInvoiceResponse?.invoice;
    const totalPaidAmount = invoiceData?.totalPaidAmount || 0;
    const tipAmount = invoiceData?.tipAmount || 0;
    return totalPaidAmount - tipAmount;
  }, [
    invoiceRawDataV2?.invoice?.totalPaidAmount,
    invoiceRawDataV2?.invoice?.tipAmount,
    getInvoiceResponse?.invoice?.totalPaidAmount,
    getInvoiceResponse?.invoice?.tipAmount
  ]);

  const isInvoiceAmountEqualToNetPaidAmount =
    netPaidAmount > 0 && Math.abs(selectedInvoice.finalAmountEditPage - netPaidAmount) < 1e-6;

  useEffect(() => {
    if (!isFromPreview && !isBackFromCreateTaxRoute && customizationsReady) {
      dispatch(updateInvoiceInitializationInProgress(true));
      getInvoiceQuery({ invoiceId: invoiceId as string, isFromEdit: true, teamId: currentTeam.id });
    } else {
      setIsLoading(false);
      dispatch(toggleIsFromPreview(false));
    }
  }, [invoiceId, customizationsReady]);

  useEffect(() => {
    if (getInvoiceResponse) {
      setIsLoading(false);
    }
  }, [getInvoiceResponse]);

  useEffect(() => {
    if (isGetInvoiceErr) {
      dispatch(updateInvoiceInitializationInProgress(false));
      setIsLoading(false);
    }
  }, [isGetInvoiceErr]);

  useEffect(() => {
    if (getInvoiceSuccess && getInvoiceResponse && !getInvoiceResponse?.invoice?.isModifiable) {
      navigate(-1);
    }
    if (getInvoiceSuccess && getInvoiceResponse) {
      dispatch(updateSelectedTNC(getInvoiceResponse.invoice.tnc));
    }
  }, [getInvoiceResponse, getInvoiceSuccess]);

  useEffect(() => {
    if (!isEditInvoiceSuccess || !invoiceEditedResponse) {
      return;
    }
    const data = invoiceEditedResponse as EditInvoiceWithRiskValidationResponse;
    const hasRiskErrors = data.isRiskValidationFailed && (data.riskValidations?.errors?.length ?? 0) > 0;
    const hasWarnings = (data.riskValidations?.warnings?.length ?? 0) > 0;
    setShowSyncConfirmationPopup(false);
    if (pendingSkipWarningsRef.current) {
      pendingSkipWarningsRef.current = false;
      handleEditInvoiceSuccess(data);
      return;
    }

    if (hasRiskErrors) {
      setRiskValidationErrors(data.riskValidations.errors);
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.backend_risk_errors_blocked,
        BackendErrorEntities: data.riskValidations.errors.map(e => e.entity).join(',')
      });
      return;
    }

    if (hasWarnings) {
      setShowRiskWarningsPopup(true);
      setRiskWarnings(data.riskValidations.warnings);
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.backend_warning_shown,
        BackendWarningCount: data.riskValidations.warnings.length
      });
      return;
    }

    handleEditInvoiceSuccess(data);
  }, [isEditInvoiceSuccess, invoiceEditedResponse, handleEditInvoiceSuccess]);

  const handleScrollToTab = (invoiceTab: InvoiceTabEnum) => {
    const tabElem = document.getElementById(invoiceTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  };

  useEffect(() => {
    handleScrollToTab(selectedInvoiceTab);
  }, [selectedInvoiceTab]);

  useEffect(() => {
    if (isInvoiceEditError) {
      const error = invoiceEditError as ErrorResponse;
      const backendErrorDetail =
        typeof error?.data?.details === 'string' ? error.data.details : error?.data?.message ?? undefined;
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.backend_error,
        ...(backendErrorDetail && { BackendErrorDetail: backendErrorDetail })
      });
      if (error?.data?.details) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: error.data.details,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Something went wrong',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      }
    }
  }, [isInvoiceEditError]);

  const hasDataChangesInInvoice = useMemo(
    () =>
      hasChangesToSync(
        selectedInvoice.invoiceCustomerEditPage,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItemsEditPage,
        selectedInvoice.initialInvoiceLineItemsEditPage,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotalEditPage,
        selectedInvoice.initialSubTotalEditPage,
        selectedInvoice.isServiceFeeEnabledEditPage,
        selectedInvoice.isServiceFeeEnabledEditPageInitial,
        selectedInvoice.currentSurchargeEditPage,
        selectedInvoice.initialSurchargeStateEditPage
      ),
    [
      selectedInvoice.invoiceCustomerEditPage,
      selectedInvoice.initialInvoiceCustomer,
      selectedInvoice.invoiceLineItemsEditPage,
      selectedInvoice.initialInvoiceLineItemsEditPage,
      selectedInvoice.selectedServiceCharge,
      selectedInvoice.initialServiceCharge,
      selectedInvoice.invoiceSubTotalEditPage,
      selectedInvoice.initialSubTotalEditPage,
      selectedInvoice.isServiceFeeEnabledEditPage,
      selectedInvoice.isServiceFeeEnabledEditPageInitial,
      selectedInvoice.currentSurchargeEditPage,
      selectedInvoice.initialSurchargeStateEditPage
    ]
  );

  const onEditAndShare = useCallback(() => {
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.attempted });
    if (isActionsBlocked) {
      setShowMerchantBlockedPopup(true);
      return;
    }
    let isPageIncomplete = false;
    let errors = InvoiceErrorFieldsDefault;
    if (selectedInvoice.invoiceLineItemsEditPage.length === 0) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }

    if (selectedInvoice.invoiceCustomerEditPage === null) {
      isPageIncomplete = true;
      errors = { ...errors, customer: true };
    }
    const invoiceMinAmount = +merchantCustomisations?.minimumAmount || invoiceMinimumAmount;
    const invoiceMaxAmount = +merchantCustomisations?.maximumAmount;
    if (selectedInvoice.finalAmountEditPage < invoiceMinAmount) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    if (selectedInvoice.finalAmountEditPage > invoiceMaxAmount && !partialPaymentEnabled) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    // Validate if invoice total is less than amount already paid
    if (netPaidAmount > 0 && selectedInvoice.finalAmountEditPage < netPaidAmount) {
      setShowInvalidTotalModal(true);
      return;
    }

    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModesEdit.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      const isAnyPaymentModeEligible = checkIfAnyPaymentModeIsEligible(
        merchantCustomisations.paymentModes,
        selectedInvoice.finalAmountEditPage,
        partialPaymentEnabled
      );
      if (!isAnyPaymentModeEligible) {
        isPageIncomplete = true;
        errors = { ...errors, paymentMethods: true };
      }
    }

    const partialPaymentRiskLimit = merchantCustomisations?.invoiceLimits?.partialPaymentRiskThresholdLimit ?? '';
    const usePartialPaymentRiskLimit =
      partialPaymentEnabled && partialPaymentRiskLimit !== '' && +partialPaymentRiskLimit > 0;
    const effectiveThreshold = usePartialPaymentRiskLimit
      ? partialPaymentRiskLimit
      : merchantCustomisations?.invoiceLimits?.invoiceValidationThreshold || '';

    const thresholdErrors = getInvoiceThresholdValidationErrors({
      totalAmount: selectedInvoice.finalAmountEditPage,
      threshold: effectiveThreshold,
      customer: selectedInvoice.invoiceCustomerEditPage
    });
    if (Object.keys(thresholdErrors).length > 0) {
      isPageIncomplete = true;
      errors = { ...errors, ...thresholdErrors };
    }

    const taxMandatoryThreshold = +merchantCustomisations?.invoiceLimits?.taxMandatoryThreshold || 0;
    const taxEncouragedThreshold = +merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold || 0;
    const hasAtLeastOneTax = appliedLineItemLevelTaxes.length > 0;
    const hasTaxSatisfied = hasAtLeastOneTax || doesServiceChargeNameContainTax(selectedInvoice.selectedServiceCharge);
    if (taxMandatoryThreshold > 0 && selectedInvoice.finalAmountEditPage >= taxMandatoryThreshold && !hasTaxSatisfied) {
      isPageIncomplete = true;
      errors = { ...errors, taxRequiredAboveMandatory: true };
    }
    const isAboveEncouragedBelowMandatoryNoTax =
      taxEncouragedThreshold > 0 &&
      selectedInvoice.finalAmountEditPage >= taxEncouragedThreshold &&
      (taxMandatoryThreshold === 0 || selectedInvoice.finalAmountEditPage < taxMandatoryThreshold) &&
      !hasTaxSatisfied;
    if (isAboveEncouragedBelowMandatoryNoTax && isPageIncomplete) {
      errors = { ...errors, taxEncouragedWarning: true };
    }

    if (isPageIncomplete) {
      lastShownValidationErrorsRef.current = errors;
      setShowInvoiceErrorsPopup({ open: true, errors });
      const validationFailedKeys = Object.entries(errors)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (errors.taxRequiredAboveMandatory) {
        logAnalyticEvent(webInvoiceCreateRiskControl, {
          ActionType: RiskControlActionType.frontend_validation_tax_mandatory,
          ValidationFailed: validationFailedKeys.join(',')
        });
      } else {
        logAnalyticEvent(webInvoiceCreateRiskControl, {
          ActionType: RiskControlActionType.frontend_validation_failed,
          ValidationFailed: validationFailedKeys.join(',')
        });
      }
      return;
    }

    if (isAboveEncouragedBelowMandatoryNoTax) {
      setShowTaxEncouragedPopup(true);
      logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_shown });
      return;
    }

    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }

    // Check if partial payment is enabled but invoice total exceeds invoice limit
    if (partialPaymentEnabled && invoiceTotal > 0 && invoiceTotal > +invoiceLimits.invoiceMaxLimit) {
      setShowInvoiceLimitExceededPopup(true);
      return;
    }

    if (partialPaymentEnabled && invoiceTotal > 0) {
      const exceedsLimit = checkIfInvoiceTotalExceedsPaymentModeLimit(invoiceTotal, invoicePaymentModes);
      if (exceedsLimit) {
        setShowPartialPaymentAvailablePopup(true);
        return;
      }
    }

    if (isInvoiceAmountEqualToNetPaidAmount) {
      setShowMarkAsPaidConfirmationPopup(true);
      return;
    }

    proceedWithInvoiceEdit();
  }, [
    isActionsBlocked,
    selectedInvoice.invoiceLineItemsEditPage.length,
    selectedInvoice.invoiceCustomerEditPage,
    selectedInvoice.finalAmountEditPage,
    merchantCustomisations?.minimumAmount,
    merchantCustomisations?.maximumAmount,
    merchantCustomisations?.invoiceLimits?.invoiceValidationThreshold,
    merchantCustomisations?.invoiceLimits?.partialPaymentRiskThresholdLimit,
    merchantCustomisations?.invoiceLimits?.taxMandatoryThreshold,
    merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold,
    appliedLineItemLevelTaxes.length,
    selectedInvoice.selectedServiceCharge?.name,
    partialPaymentEnabled,
    selectedInvoice.invoicePaymentModesEdit,
    merchantCustomisations?.paymentModes,
    invoiceTotal,
    invoiceLimits.invoiceMaxLimit,
    invoicePaymentModes,
    isInvoiceFromJob,
    jobData,
    estimateNumber,
    isEstimatePending,
    hasDataChangesInInvoice,
    netPaidAmount
  ]);

  const handleEditInvoice = (sendEmailToCustomer = false, skipWarnings?: boolean) => {
    const latestSelectedInvoice = selectedInvoiceRef.current;
    const tnc = getInvoiceResponse?.invoice?.tnc;

    const v2Payload = getEditInvoiceV2Payload(
      latestSelectedInvoice,
      currentTeam,
      {
        enabled: tnc?.enabled || false,
        tncId: tnc?.tncId || 0
      },
      merchantCustomisations,
      sendEmailToCustomer
    );

    const payload = {
      invoiceId: invoiceId as string,
      invoicePayload: v2Payload,
      ...(skipWarnings !== undefined && { skipWarnings })
    };

    if (skipWarnings) {
      pendingSkipWarningsRef.current = true;
    }
    editInvoiceMutation({ teamId: currentTeam.id.toString(), payload });
  };

  const proceedWithInvoiceEdit = () => {
    if (
      ((isInvoiceFromJob && jobData[0]?.isEditable) || (estimateNumber && isEstimatePending)) &&
      hasDataChangesInInvoice
    ) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  };

  const handleUpdateToCustomerAndSave = (sendUpdateToCustomer = false) => {
    setShowSendUpdateToCustomerModal(false);
    handleEditInvoice(sendUpdateToCustomer);
  };

  const handleContinueMarkAsPaidConfirmation = () => {
    setShowMarkAsPaidConfirmationPopup(false);
    proceedWithInvoiceEdit();
  };

  const handleContinueFromSyncConfirmation = () => {
    setShowSyncConfirmationPopup(false);
    if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  };

  const handleOnCreateInvoiceWithoutSelfCheckout = useCallback(() => {
    setShowNoSelfCheckoutPopup(false);

    if (isInvoiceAmountEqualToNetPaidAmount) {
      setShowMarkAsPaidConfirmationPopup(true);
      return;
    }

    if ((isInvoiceFromJob || (estimateNumber && isEstimatePending)) && hasDataChangesInInvoice) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  }, [
    isInvoiceAmountEqualToNetPaidAmount,
    isInvoiceFromJob,
    estimateNumber,
    isEstimatePending,
    hasDataChangesInInvoice,
    hasCustomerEmail
  ]);

  const handleOnEditPaymentMethods = useCallback(() => {
    handleCloseNoSelfCheckoutPopup();
    handleScrollToTab(InvoiceTabEnum.PAYMENT_OPTIONS);
  }, []);

  const handleCloseNoSelfCheckoutPopup = useCallback(() => {
    setShowNoSelfCheckoutPopup(false);
  }, []);

  const handleCloseInvoiceErrorsPopup = useCallback(() => {
    const lastErrors = lastShownValidationErrorsRef.current;
    setShowInvoiceErrorsPopup({ open: false, errors: InvoiceErrorFieldsDefault });
    if (lastErrors) {
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.frontend_validation_dialog_closed,
        ValidationFailed: Object.entries(lastErrors)
          .filter(([, v]) => v)
          .map(([k]) => k)
          .join(',')
      });
      lastShownValidationErrorsRef.current = null;
    }
  }, []);

  const handleCloseTaxEncouragedPopup = useCallback(() => {
    setShowTaxEncouragedPopup(false);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_closed });
  }, []);

  const handleProceedFromTaxEncouraged = useCallback(() => {
    setShowTaxEncouragedPopup(false);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_continued });
    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModesEdit.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }
    if (partialPaymentEnabled && invoiceTotal > 0 && invoiceTotal > +invoiceLimits.invoiceMaxLimit) {
      setShowInvoiceLimitExceededPopup(true);
      return;
    }
    if (partialPaymentEnabled && invoiceTotal > 0) {
      const exceedsLimit = checkIfInvoiceTotalExceedsPaymentModeLimit(invoiceTotal, invoicePaymentModes);
      if (exceedsLimit) {
        setShowPartialPaymentAvailablePopup(true);
        return;
      }
    }
    if (
      ((isInvoiceFromJob && jobData[0]?.isEditable) || (estimateNumber && isEstimatePending)) &&
      hasDataChangesInInvoice
    ) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  }, [
    selectedInvoice.invoicePaymentModesEdit,
    partialPaymentEnabled,
    invoiceTotal,
    invoiceLimits.invoiceMaxLimit,
    invoicePaymentModes,
    isInvoiceFromJob,
    jobData,
    estimateNumber,
    isEstimatePending,
    hasDataChangesInInvoice,
    hasCustomerEmail,
    selectedInvoice.invoiceCustomerEditPage
  ]);

  const handleAddTaxFromTaxEncouraged = useCallback(() => {
    setShowTaxEncouragedPopup(false);
    setSelectedInvoiceTab(InvoiceTabEnum.LINE_ITEMS);
    handleScrollToTab(InvoiceTabEnum.LINE_ITEMS);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_add_tax });
  }, []);

  const handleClosePartialPaymentAvailablePopup = useCallback(() => {
    setShowPartialPaymentAvailablePopup(false);
  }, []);

  const handleSaveAnyway = useCallback(() => {
    handleClosePartialPaymentAvailablePopup();
    if (
      ((isInvoiceFromJob && jobData[0]?.isEditable) || (estimateNumber && isEstimatePending)) &&
      hasDataChangesInInvoice
    ) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  }, [
    handleClosePartialPaymentAvailablePopup,
    isInvoiceFromJob,
    jobData,
    estimateNumber,
    isEstimatePending,
    hasDataChangesInInvoice
  ]);

  const handleEditInvoiceFromPartialPaymentPopup = useCallback(() => {
    handleClosePartialPaymentAvailablePopup();
  }, [handleClosePartialPaymentAvailablePopup]);

  const handleCloseInvoiceLimitExceededPopup = useCallback(() => {
    setShowInvoiceLimitExceededPopup(false);
  }, []);

  const handleEditInvoiceFromLimitExceededPopup = useCallback(() => {
    handleCloseInvoiceLimitExceededPopup();
  }, [handleCloseInvoiceLimitExceededPopup]);

  const customerRiskError = useMemo(() => {
    const customerErrors = riskValidationErrors.filter(
      e => e.entity?.toLowerCase() === RISK_VALIDATION_ENTITY.CUSTOMER
    );
    return customerErrors.length > 0 ? customerErrors.map(e => e.message).join(' ') : undefined;
  }, [riskValidationErrors]);

  const lineItemRiskError = useMemo(() => {
    const lineItemErrors = riskValidationErrors.filter(
      e => e.entity?.toLowerCase() === RISK_VALIDATION_ENTITY.LINE_ITEM
    );
    return lineItemErrors.length > 0 ? lineItemErrors.map(e => e.message).join(' ') : undefined;
  }, [riskValidationErrors]);

  useEffect(() => {
    if (customerRiskError) {
      handleScrollToTab(InvoiceTabEnum.BILL_TO);
      return;
    }
    if (lineItemRiskError) {
      handleScrollToTab(InvoiceTabEnum.LINE_ITEMS);
    }
  }, [customerRiskError, lineItemRiskError]);

  const handleClearCustomerRiskError = useCallback(() => {
    setRiskValidationErrors(prev => prev.filter(e => e.entity?.toLowerCase() !== RISK_VALIDATION_ENTITY.CUSTOMER));
  }, []);

  const handleClearLineItemRiskError = useCallback(() => {
    setRiskValidationErrors(prev => prev.filter(e => e.entity?.toLowerCase() !== RISK_VALIDATION_ENTITY.LINE_ITEM));
  }, []);

  const handleRiskWarningsSaveAnyway = useCallback(() => {
    setShowRiskWarningsPopup(false);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.backend_warning_confirmed });
    handleEditInvoice(false, true);
  }, []);

  const handleRiskWarningsGotIt = useCallback(() => {
    setShowRiskWarningsPopup(false);
    setRiskWarnings([]);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.backend_warning_closed });
  }, []);

  const editAndShareHandler = useCallback(
    throttle(() => {
      onEditAndShare();
    }, 800),
    [onEditAndShare]
  );

  const onBackIconClick = () => {
    dispatch(resetInvoiceEditSlice());
    navigate(-1);
  };

  const handleSignatureStateChange = (value: boolean) => {
    dispatch(updateIsSignatureEnabledEditPage(value));
  };

  const handleInvoiceReferenceNumberChange = (value: string) => {
    dispatch(updateInvoiceReferenceNumberEditPage(value));
  };

  const checkIsSignatureDisabled = useMemo(() => {
    if (customizationsReady) {
      return merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON;
    }
    return false;
  }, [customizationsReady, merchantCustomisations?.customerSignatureConfig]);

  const syncPopupTitle = useMemo(() => {
    const showJob = isInvoiceFromJob || !!invoiceFromJobNumber;
    const showEstimate = !!estimateNumber && isEstimatePending;
    const showAnd = showJob && showEstimate;

    const jobPart = showJob ? 'job' : '';
    const andPart = showAnd ? 'and' : '';
    const estimatePart = showEstimate ? 'estimate' : '';

    return `Save and sync changes to the related ${jobPart} ${andPart} ${estimatePart}?`.replace(/\s+/g, ' ').trim();
  }, [isInvoiceFromJob, invoiceFromJobNumber, estimateNumber, isEstimatePending]);

  const syncPopupSubtitle = useMemo(() => {
    const showJob = isInvoiceFromJob || !!invoiceFromJobNumber;
    const showEstimate = !!estimateNumber && isEstimatePending;
    const showAnd = showJob && showEstimate;

    const jobText = showJob ? `job ${invoiceFromJobNumber}` : '';
    const estimateText = showEstimate ? `estimate ${estimateNumber}` : '';
    const andPart = showAnd ? ' and ' : '';

    const base = 'Changes in your invoices will be applied to the associated';
    const text = `${base} ${jobText}${andPart}${estimateText} as well.`;
    return text.replace(/\s+/g, ' ').trim();
  }, [isInvoiceFromJob, invoiceFromJobNumber, estimateNumber, isEstimatePending]);

  const onPreviewButtonClick = () => {
    let isPageIncomplete = false;
    if (selectedInvoice.invoiceLineItemsEditPage.length === 0) {
      isPageIncomplete = true;
      dispatch(addInvoiceValidationErrorEditPage(minimumLineItemsError));
    }

    if (selectedInvoice.invoiceCustomerEditPage === null) {
      isPageIncomplete = true;
      dispatch(enableCustomerNotSelectedErrorEditPage());
    }

    if (isPageIncomplete) {
      return;
    }
    goToPreviewPage();
  };

  if (isLoading || invoiceInitInProgress)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
      </div>
    );

  if (!isLoading && isGetInvoiceErr)
    return (
      <div className="flex h-full w-full justify-center">
        <GenericError handleErrorBack={() => navigate(-1)} />
      </div>
    );

  return (
    <EditInvoiceContext.Provider value={{ selectedInvoiceTab, setSelectedInvoiceTab }}>
      <div className="flex h-full w-full flex-col border">
        <PreviewBar
          className="border border-borderGray"
          title="Edit Invoice"
          onPrimaryClick={editAndShareHandler}
          onReturn={onBackIconClick}
          primaryBtnTitle="Save & Share"
          secondaryBtnTitle="Preview"
          disablePrimaryBtn={false}
          isPrimaryBtnLoading={isEditInvoiceLoading}
          onSecondaryClick={onPreviewButtonClick}
          primaryBtnId="save-and-share-invoice-button"
          secondaryBtnId="edit-invoice-preview-button"
        />
        <div className="flex h-full w-full justify-center overflow-hidden bg-primary">
          <div
            className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-12
          pr-[50px] lg:flex lg:w-[24rem] 1439px:w-[31.25rem]">
            <InvoicesTabSideNav context={EditInvoiceContext} isInvoiceEdit />
          </div>

          <div
            id="scrollable div"
            className="customNormalScroll flex grow flex-col space-y-[30px]
           overflow-y-auto p-12 pl-[50px] 1439px:space-y-11">
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.BILL_TO}>
              <EditInvoiceCustomer
                context={EditInvoiceContext}
                customerError={customerRiskError}
                onClearCustomerRiskError={handleClearCustomerRiskError}
              />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.INVOICE_OPTIONS}>
              <InvoiceOptionsSectionEdit context={EditInvoiceContext} />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.LINE_ITEMS}>
              <LineItemsSectionEdit
                context={EditInvoiceContext}
                lineItemError={lineItemRiskError}
                onClearLineItemRiskError={handleClearLineItemRiskError}
              />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.NOTES_ATTACHMENTS}>
              <NotesAttachmentsSectionEdit context={EditInvoiceContext} />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_OPTIONS}>
              <PaymentOptionsSectionEdit isInvoiceEdit={true} context={EditInvoiceContext} />
            </div>
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_REMINDERS}>
              <InvoiceFormRemindersSection isInvoiceEdit={true} context={EditInvoiceContext} />
            </div>
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.MORE_OPTIONS}>
              <MoreOptionsSection
                signatureState={selectedIsSignatureEnabled}
                updateSignatureState={handleSignatureStateChange}
                context={EditInvoiceContext}
                isDisabled={checkIsSignatureDisabled}
                id="edit-invoice-allow-sign-toggle-switch"
                invoiceReferenceNumber={invoiceReferenceNumberEditPage}
                updateInvoiceReferenceNumber={handleInvoiceReferenceNumberChange}
              />
            </div>
          </div>
        </div>
      </div>
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title={syncPopupTitle}
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={syncPopupSubtitle}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleContinueFromSyncConfirmation}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => <CrossIconvector height={18} width={18} title="sync-popup-close" />}
          isActionInProgress={isEditInvoiceLoading}
          height="h-[262px]"
          className="!px-5 !py-6"
        />
      )}
      {showSendUpdateToCustomerModal && (
        <SendUpdateModal
          showModal={showSendUpdateToCustomerModal}
          onYesClick={() => handleUpdateToCustomerAndSave(true)}
          onNoClick={handleUpdateToCustomerAndSave}
          onClose={() => setShowSendUpdateToCustomerModal(false)}
          isActionInProgress={isEditInvoiceLoading}
        />
      )}
      {showNoSelfCheckoutPopup && (
        <NoSelfCheckoutPopup
          isEdit={true}
          onContinue={handleOnCreateInvoiceWithoutSelfCheckout}
          onEditPaymentMethods={handleOnEditPaymentMethods}
          onClose={handleCloseNoSelfCheckoutPopup}
          isActionLoading={isEditInvoiceLoading}
        />
      )}
      {showInvoiceErrorsPopup.open && (
        <InvoiceErrorsPopup
          onClose={handleCloseInvoiceErrorsPopup}
          errorFields={showInvoiceErrorsPopup.errors}
          maximumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMaxLimit : +merchantCustomisations?.maximumAmount || 0
          }
          minimumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMinLimit : +merchantCustomisations?.minimumAmount || 0
          }
          taxEncouragedThreshold={merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold}
          taxMandatoryThreshold={merchantCustomisations?.invoiceLimits?.taxMandatoryThreshold}
          onContinue={handleCloseInvoiceErrorsPopup}
        />
      )}
      {showTaxEncouragedPopup && (
        <InvoiceTaxEncouragedPopup
          onClose={handleCloseTaxEncouragedPopup}
          onAddTax={handleAddTaxFromTaxEncouraged}
          onProceedAnyway={handleProceedFromTaxEncouraged}
          isEdit={true}
          taxEncouragedThreshold={merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold}
          isLoading={isEditInvoiceLoading}
        />
      )}
      {showMerchantBlockedPopup && (
        <BlockedInfoPopup isOpen={showMerchantBlockedPopup} onClose={() => setShowMerchantBlockedPopup(false)} />
      )}
      {showPartialPaymentAvailablePopup && (
        <PartialPaymentAvailablePopup
          onClose={handleClosePartialPaymentAvailablePopup}
          paymentModes={invoicePaymentModes}
          onCreateAnyway={handleSaveAnyway}
          onEditInvoice={handleEditInvoiceFromPartialPaymentPopup}
        />
      )}
      {showInvoiceLimitExceededPopup && (
        <InvoiceLimitExceededPopup
          onClose={handleCloseInvoiceLimitExceededPopup}
          invoiceLimits={invoiceLimits}
          onEditInvoice={handleEditInvoiceFromLimitExceededPopup}
        />
      )}
      {showInvalidTotalModal && (
        <InvalidInvoiceTotalModal
          open={showInvalidTotalModal}
          onClose={() => setShowInvalidTotalModal(false)}
          totalPaid={netPaidAmount}
          minimumTotal={netPaidAmount + 0.01}
        />
      )}
      {showMarkAsPaidConfirmationPopup && (
        <GenericModal
          showModal={true}
          title="Invoice will be marked as paid"
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={
            'This invoice amount matches the amount already paid. ' +
            'If you continue, this invoice will be marked as fully paid.'
          }
          primaryBtnLabel="Continue"
          primaryBtnId="confirm-mark-as-paid"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleContinueMarkAsPaidConfirmation}
          secondaryBtnId="cancel-mark-as-paid"
          secondaryBtnStyle="border-borderGray shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowMarkAsPaidConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => <CrossIconvector height={18} width={18} title="mark-as-paid-popup-close" />}
          isActionInProgress={isEditInvoiceLoading}
          containerWidth="370px"
          height="h-[266px]"
          className="!p-6"
        />
      )}
      <InvoiceRiskWarningsPopup
        open={showRiskWarningsPopup}
        warnings={riskWarnings}
        onCreateAnyway={handleRiskWarningsSaveAnyway}
        onGotIt={handleRiskWarningsGotIt}
        isEdit={true}
        isLoading={isEditInvoiceLoading}
      />
    </EditInvoiceContext.Provider>
  );
};

export default EditInvoice;
